/**
 * Chauffeur Dashboard — Refonte Mobile EPIC 1
 * Content-only: header & nav provided by ChauffeurMobileLayout.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { Link, useOutletContext } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { useActionGuard } from '../hooks/useActionGuard';
import axios from 'axios';
import {
  Calendar, Car, MapPin, User, Clock, CheckCircle2,
  ChevronRight, Loader2, RefreshCw, AlertTriangle, Navigation,
  Phone, PlayCircle, Flag, Bell
} from 'lucide-react';
import config from 'config';

const API_URL = config.API_URL;

const STATUS_BADGE = {
  assigned: { label: 'Assignée', cls: 'bg-blue-500/20 text-blue-400' },
  accepted: { label: 'Acceptée', cls: 'bg-purple-500/20 text-purple-400' },
  confirmed: { label: 'Confirmée', cls: 'bg-green-500/20 text-green-400' },
  en_route: { label: 'En route', cls: 'bg-yellow-500/20 text-yellow-400' },
  on_site: { label: 'Sur place', cls: 'bg-orange-500/20 text-orange-400' },
  client_on_board: { label: 'Client à bord', cls: 'bg-cyan-500/20 text-cyan-400' },
  completed: { label: 'Terminée', cls: 'bg-green-500/20 text-green-400' },
  cancelled: { label: 'Annulée', cls: 'bg-red-500/20 text-red-400' },
};

const NEXT_BTN = {
  accepted: { label: 'Accepter la mission', icon: CheckCircle2, color: 'bg-green-600 hover:bg-green-700' },
  en_route: { label: 'Démarrer - En route', icon: Navigation, color: 'bg-blue-600 hover:bg-blue-700' },
  on_site: { label: 'Arrivé sur place', icon: MapPin, color: 'bg-orange-600 hover:bg-orange-700' },
  client_on_board: { label: 'Client à bord', icon: User, color: 'bg-cyan-600 hover:bg-cyan-700' },
  completed: { label: 'Terminer la mission', icon: Flag, color: 'bg-green-600 hover:bg-green-700' },
};

const fmt = (iso) => {
  if (!iso) return '-';
  return new Date(iso).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
};
const fmtTime = (iso) => {
  if (!iso) return '-';
  return new Date(iso).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
};

const ChauffeurDashboard = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [currentMission, setCurrentMission] = useState(null);
  const [upcomingMissions, setUpcomingMissions] = useState([]);
  const [proposals, setProposals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const statusAction = useActionGuard();

  const fetchDashboardData = useCallback(async () => {
    setRefreshing(true);
    const token = localStorage.getItem('access_token');
    try {
      const [statsRes, currentRes, upcomingRes, proposalsRes] = await Promise.all([
        axios.get(`${API_URL}/api/chauffeur/dashboard`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API_URL}/api/chauffeur/dashboard/current-mission`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API_URL}/api/chauffeur/missions?period=upcoming&per_page=5`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API_URL}/api/dispatch/pro/chauffeur/missions/pending`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      setStats(statsRes.data);
      setCurrentMission(currentRes.data.mission);
      setUpcomingMissions(upcomingRes.data.missions || []);
      setProposals(proposalsRes.data?.missions || []);
    } catch (err) {
      console.error('Failed to fetch dashboard data:', err);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);

  const handleUpdateStatus = statusAction.guard(async (missionId, newStatus) => {
    const token = localStorage.getItem('access_token');
    await axios.post(
      `${API_URL}/api/chauffeur/missions/${missionId}/update-status`,
      { new_status: newStatus },
      { headers: { Authorization: `Bearer ${token}` } }
    ).catch(err => { alert(err.response?.data?.detail || 'Erreur'); throw err; });
    fetchDashboardData();
  });

  const badge = (status) => {
    const c = STATUS_BADGE[status] || STATUS_BADGE.assigned;
    return <span className={`px-2 py-0.5 rounded text-xs ${c.cls}`}>{c.label}</span>;
  };

  const nextBtn = (mission) => {
    if (!mission?.next_status) return null;
    const cfg = NEXT_BTN[mission.next_status];
    if (!cfg) return null;
    return (
      <Button
        onClick={() => handleUpdateStatus(mission.id, mission.next_status)}
        disabled={statusAction.loading}
        className={`w-full ${cfg.color} text-white min-h-[44px] touch-manipulation`}
        data-testid={`btn-${mission.next_status}`}
      >
        {statusAction.loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <cfg.icon className="w-4 h-4 mr-2" />}
        {cfg.label}
      </Button>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-7 h-7 text-[#C9A24D] animate-spin" />
      </div>
    );
  }

  return (
    <div className="px-4 py-4 max-w-screen-lg mx-auto" data-testid="chauffeur-dashboard">
      {/* Refresh */}
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-base font-semibold text-white">Tableau de bord</h2>
        <Button
          variant="ghost"
          size="sm"
          onClick={fetchDashboardData}
          disabled={refreshing}
          className="text-[#E5E5E5]/50 hover:text-[#C9A24D] min-h-[44px] min-w-[44px] touch-manipulation"
          data-testid="refresh-dashboard"
        >
          <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
        </Button>
      </div>

      {/* Stats — 2×2 grid */}
      <div className="grid grid-cols-2 gap-3 mb-5">
        {[
          { label: 'Missions du jour', val: stats?.missions_today || 0, icon: Calendar, color: 'text-[#C9A24D]' },
          { label: 'À venir', val: stats?.missions_upcoming || 0, icon: Clock, color: 'text-[#C9A24D]' },
          { label: 'En cours', val: stats?.missions_in_progress || 0, icon: Navigation, color: 'text-yellow-400' },
          { label: 'Terminées', val: stats?.total_missions_completed || 0, icon: CheckCircle2, color: 'text-green-400' },
        ].map((s) => (
          <Card key={s.label} className="bg-[#161616] border-white/6">
            <CardContent className="p-3.5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-[#E5E5E5]/50 text-[11px] leading-tight">{s.label}</p>
                  <p className={`text-2xl font-bold mt-0.5 ${s.color}`}>{s.val}</p>
                </div>
                <s.icon className={`w-7 h-7 ${s.color} opacity-25`} />
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Current Mission */}
      <Card className="bg-gradient-to-br from-[#C9A24D]/8 to-[#161616] border-[#C9A24D]/15 mb-5">
        <CardContent className="p-4">
          <h3 className="text-sm font-semibold text-white flex items-center gap-2 mb-3">
            <PlayCircle className="w-4 h-4 text-[#C9A24D]" /> Mission en cours
          </h3>
          {currentMission ? (
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-[#C9A24D] font-mono text-sm">{currentMission.booking_ref}</span>
                {badge(currentMission.status)}
              </div>

              <div className="space-y-2.5 p-3 bg-[#0B0B0B]/60 rounded-lg">
                <div className="flex items-start gap-2.5">
                  <div className="w-2.5 h-2.5 rounded-full bg-green-500 mt-1.5 shrink-0" />
                  <div className="min-w-0">
                    <p className="text-[#E5E5E5]/40 text-[10px]">Départ</p>
                    <p className="text-[#E5E5E5] text-sm truncate">{currentMission.pickup_address}</p>
                  </div>
                </div>
                <div className="flex items-start gap-2.5">
                  <div className="w-2.5 h-2.5 rounded-full bg-red-500 mt-1.5 shrink-0" />
                  <div className="min-w-0">
                    <p className="text-[#E5E5E5]/40 text-[10px]">Arrivée</p>
                    <p className="text-[#E5E5E5] text-sm truncate">{currentMission.dropoff_address}</p>
                  </div>
                </div>
              </div>

              <div className="flex items-center justify-between text-sm">
                <div className="flex items-center gap-1.5 text-[#E5E5E5]">
                  <Clock className="w-3.5 h-3.5 text-[#C9A24D]" />
                  <span className="text-xs">{fmt(currentMission.pickup_datetime)}</span>
                </div>
                {currentMission.client_phone && (
                  <a
                    href={`tel:${currentMission.client_phone}`}
                    className="flex items-center gap-1.5 text-[#C9A24D] text-xs min-h-[44px] touch-manipulation"
                  >
                    <Phone className="w-3.5 h-3.5" /> {currentMission.client_name}
                  </a>
                )}
              </div>

              {nextBtn(currentMission)}

              <Link
                to={`/chauffeur/missions/${currentMission.id}`}
                className="block text-center text-[#C9A24D] text-xs hover:underline py-1"
              >
                Voir les détails →
              </Link>
            </div>
          ) : (
            <div className="text-center py-8">
              <Car className="w-10 h-10 mx-auto mb-2 text-[#E5E5E5]/15" />
              <p className="text-[#E5E5E5]/50 text-sm">Aucune mission en cours</p>
              {upcomingMissions.length > 0 && (
                <p className="text-[#C9A24D] text-xs mt-1">
                  Prochaine à {fmtTime(upcomingMissions[0]?.pickup_datetime)}
                </p>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Missions reçues (pending proposals) */}
      {proposals.length > 0 && (
        <>
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-white flex items-center gap-2">
              <Bell className="w-4 h-4 text-amber-400" /> Missions reçues
              <span className="w-5 h-5 rounded-full bg-red-500 text-white text-[9px] font-bold flex items-center justify-center">
                {proposals.length}
              </span>
            </h3>
            <Link
              to="/chauffeur/missions?period=received"
              className="text-[#C9A24D] text-xs hover:underline flex items-center gap-0.5 min-h-[44px] touch-manipulation"
              data-testid="voir-tout-received"
            >
              Voir tout <ChevronRight className="w-3.5 h-3.5" />
            </Link>
          </div>
          <div className="space-y-2.5 mb-5">
            {proposals.slice(0, 3).map((p) => (
              <Link key={p.id} to={`/chauffeur/missions?period=received`} className="block">
                <Card className="bg-[#161616] border-amber-500/15 hover:border-amber-500/30 active:bg-amber-500/5 transition-colors" data-testid={`dashboard-proposal-${p.id}`}>
                  <CardContent className="p-3.5">
                    <div className="flex items-center justify-between mb-1.5">
                      <div className="flex items-center gap-2 min-w-0">
                        <span className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-amber-500/20 text-amber-400">{p.type}</span>
                        <span className="text-[#C9A24D] font-mono text-xs">{p.ref_id}</span>
                      </div>
                      <span className="text-white font-semibold text-sm shrink-0 ml-2">
                        {fmtTime(p.start_time)}
                      </span>
                    </div>
                    <div className="flex items-start gap-2 mb-1">
                      <MapPin className="w-3.5 h-3.5 text-emerald-400 mt-0.5 shrink-0" />
                      <p className="text-[#E5E5E5]/70 text-xs truncate">{p.pickup_address || '\u2014'}</p>
                    </div>
                    {p.dropoff_address && (
                      <div className="flex items-start gap-2 ml-5">
                        <MapPin className="w-3 h-3 text-red-400 mt-0.5 shrink-0" />
                        <p className="text-[#E5E5E5]/40 text-[11px] truncate">{p.dropoff_address}</p>
                      </div>
                    )}
                    <div className="flex items-center gap-3 mt-1.5 text-[10px] text-[#E5E5E5]/35">
                      <span className="flex items-center gap-1"><User className="w-3 h-3" />{p.client_name || '\u2014'}</span>
                      <span>{p.passenger_count || 0} pax</span>
                      {p.vehicle_category && (
                        <span className="flex items-center gap-1 text-[#C9A24D]/60"><Car className="w-3 h-3" />{p.vehicle_category}</span>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </Link>
            ))}
            {proposals.length > 3 && (
              <Link to="/chauffeur/missions?period=received" className="block text-center text-amber-400 text-xs py-2 hover:underline">
                + {proposals.length - 3} autre(s) mission(s)
              </Link>
            )}
          </div>
        </>
      )}

      {/* Upcoming Missions */}
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold text-white flex items-center gap-2">
          <Calendar className="w-4 h-4 text-[#C9A24D]" /> Missions à venir
        </h3>
        <Link
          to="/chauffeur/missions?period=upcoming"
          className="text-[#C9A24D] text-xs hover:underline flex items-center gap-0.5 min-h-[44px] touch-manipulation"
        >
          Voir tout <ChevronRight className="w-3.5 h-3.5" />
        </Link>
      </div>

      {upcomingMissions.length === 0 ? (
        <Card className="bg-[#161616] border-white/6">
          <CardContent className="py-10 text-center">
            <Calendar className="w-9 h-9 mx-auto mb-2 text-[#E5E5E5]/15" />
            <p className="text-[#E5E5E5]/40 text-sm">Aucune mission planifiée</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2.5">
          {upcomingMissions.map((m) => (
            <Link key={m.id} to={`/chauffeur/missions/${m.id}`} className="block">
              <Card className="bg-[#161616] border-white/6 hover:border-[#C9A24D]/20 active:bg-[#C9A24D]/5 transition-colors">
                <CardContent className="p-3.5">
                  <div className="flex items-center justify-between mb-1.5">
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="text-[#C9A24D] font-mono text-xs">{m.booking_ref}</span>
                      {badge(m.status)}
                    </div>
                    <span className="text-white font-semibold text-sm shrink-0 ml-2">
                      {fmtTime(m.pickup_datetime)}
                    </span>
                  </div>
                  <p className="text-[#E5E5E5]/50 text-xs truncate">
                    {m.pickup_city} → {m.dropoff_city}
                  </p>
                  <div className="flex items-center gap-3 mt-1.5 text-[10px] text-[#E5E5E5]/35">
                    <span className="capitalize">{m.vehicle_category}</span>
                    <span>{m.passengers} pax</span>
                    {m.has_special_requests && (
                      <span className="text-yellow-400 flex items-center gap-0.5">
                        <AlertTriangle className="w-3 h-3" /> Instructions
                      </span>
                    )}
                  </div>
                </CardContent>
              </Card>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
};

export default ChauffeurDashboard;
